"use client";

/**
 * ComposedCard – renders a tutor-composed exercise built from the interaction
 * primitive vocabulary (Level 1 generative UX). The tutor invents the
 * mechanics; each block reports learner interactions with a block label so
 * the tutor knows exactly what happened where.
 */
import type { WhiteboardRenderState } from "@/lib/whiteboard/boardState";
import type { ComposedBlock, ComposedSpec } from "@/lib/whiteboard/spec";
import { showWhiteboardSectionLabel } from "@/shared/whiteboard-headings";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LessonFeedback } from "./LessonPrimitives";
import { LessonHeading, useLessonPresentation } from "./LessonPrimitives";
import { WhiteboardHeadingContext } from "./WhiteboardHeadingContext";
import { WhiteboardWriteInText } from "./WhiteboardWriteInText";
import {
  type BlockEvent,
  BucketSortBlock,
  ContrastPairBlock,
  DiscourseFunctionBlock,
  HighlightTargetBlock,
  ListeningDiscriminationBlock,
  MorphologyGridBlock,
  OrderingBlock,
  PairMatchBlock,
  PhraseBankRoleplayBlock,
  PronunciationFocusBlock,
  RevealBlock,
  SentenceRepairBlock,
  SliderBlock,
  SlotSentenceBlock,
  TapGroupBlock,
  TextBlock,
  TimelineBuilderBlock,
  TransformationFrameBlock,
  TranslationChoiceBlock,
} from "./composedBlocks";
import { type WhiteboardCardEventHandler, whiteboardWriteDelayStyle } from "./types";

interface ComposedCardProps {
  spec: ComposedSpec;
  renderState: WhiteboardRenderState;
  onEvent?: WhiteboardCardEventHandler;
}

const NON_COMPLETABLE_BLOCK_TYPES = new Set<ComposedBlock["type"]>(["text", "reveal", "slider"]);

export const ComposedCard: React.FC<ComposedCardProps> = ({ spec, renderState, onEvent }) => {
  const headings = useContext(WhiteboardHeadingContext);
  const lesson = useLessonPresentation();
  const blockEntries = useMemo(
    () =>
      spec.blocks.map((block, index) => {
        const label = ("label" in block && block.label) || `${block.type} #${index + 1}`;
        return {
          block,
          index,
          key: `${index}:${label}`,
          label,
          required: !NON_COMPLETABLE_BLOCK_TYPES.has(block.type),
        };
      }),
    [spec.blocks]
  );
  const requiredBlockKeys = useMemo(
    () => blockEntries.filter((entry) => entry.required).map((entry) => entry.key),
    [blockEntries]
  );
  const blockLabelByKey = useMemo(
    () => new Map(blockEntries.map((entry) => [entry.key, entry.label])),
    [blockEntries]
  );
  const [resolvedBlockKeys, setResolvedBlockKeys] = useState<Set<string>>(new Set());
  const resolvedBlockKeysRef = useRef(resolvedBlockKeys);

  useEffect(() => {
    const reset = new Set<string>();
    resolvedBlockKeysRef.current = reset;
    setResolvedBlockKeys(reset);
  }, [spec.id]);

  useEffect(() => {
    resolvedBlockKeysRef.current = resolvedBlockKeys;
  }, [resolvedBlockKeys]);

  const makeBlockHandler =
    (blockKey: string, blockLabel: string, blockType: string, required: boolean) =>
    (event: BlockEvent): void => {
      const isWorkingEvent = event.event === "working";
      const childResolved =
        event.resolved ??
        (event.correct === true ? true : event.correct === false ? false : undefined);
      const blockCompleted = required && childResolved === true;
      let nextResolvedBlockKeys = resolvedBlockKeysRef.current;

      if (blockCompleted && !nextResolvedBlockKeys.has(blockKey)) {
        nextResolvedBlockKeys = new Set(nextResolvedBlockKeys).add(blockKey);
        resolvedBlockKeysRef.current = nextResolvedBlockKeys;
        setResolvedBlockKeys(nextResolvedBlockKeys);
      }

      const totalRequired = requiredBlockKeys.length;
      const completedCount = nextResolvedBlockKeys.size;
      const allRequiredDone = totalRequired > 0 && completedCount >= totalRequired;
      const remainingLabels = requiredBlockKeys
        .filter((key) => !nextResolvedBlockKeys.has(key))
        .map((key) => blockLabelByKey.get(key) ?? key);
      const progress = isWorkingEvent
        ? event.progress
        : totalRequired > 1 && required
          ? childResolved === false
            ? `${completedCount}/${totalRequired} board parts complete; ${blockLabel} needs retry`
            : `${completedCount}/${totalRequired} board parts complete${
                remainingLabels.length ? `; remaining: ${remainingLabels.join(", ")}` : ""
              }`
          : event.progress;
      const boardResolved = isWorkingEvent
        ? false
        : totalRequired > 1 && required
          ? childResolved === true
            ? allRequiredDone
            : childResolved
          : childResolved;

      onEvent?.({
        event: isWorkingEvent ? "working" : boardResolved === true ? "completed" : "answered",
        block: blockLabel,
        ...event,
        action: event.action ?? blockType,
        target: event.target ?? blockLabel,
        progress,
        resolved: boardResolved,
      });
    };

  return (
    <div className="space-y-5">
      {renderState.highlightTargets.length > 0 && (
        <LessonFeedback
          className="rounded-xl border border-[#FD954F]/35 bg-[#FD954F]/10 px-3 py-2 text-sm font-semibold text-[#FFD9B8]"
          tone={"hint"}
        >
          Tutor focus: {renderState.highlightTargets.join(", ")}
        </LessonFeedback>
      )}
      {spec.title &&
        headings.showTitle &&
        (lesson ? (
          <LessonHeading as="p" aria-label={spec.title}>
            {spec.title}
          </LessonHeading>
        ) : (
          <WhiteboardWriteInText
            as="p"
            text={spec.title}
            data-whiteboard-lesson-title
            data-whiteboard-write-line
            style={whiteboardWriteDelayStyle(0)}
            className="text-lg sm:text-xl font-semibold leading-snug text-[#F9F9F9]"
          />
        ))}
      {blockEntries.map(({ block, index, key, label, required }) => {
        const handler = makeBlockHandler(key, label, block.type, required);
        const writeIndex = index + (spec.title ? 1 : 0);
        const blockElement = (() => {
          switch (block.type) {
            case "text":
              return <TextBlock key={index} block={block} />;
            case "tap_group":
              return (
                <TapGroupBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "slot_sentence":
              return (
                <SlotSentenceBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "bucket_sort":
              return (
                <BucketSortBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "highlight_target":
              return (
                <HighlightTargetBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "ordering":
              return (
                <OrderingBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "slider":
              return <SliderBlock key={index} block={block} onEvent={handler} />;
            case "reveal":
              return <RevealBlock key={index} block={block} onEvent={handler} />;
            case "sentence_repair":
              return (
                <SentenceRepairBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "pair_match":
              return (
                <PairMatchBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "pronunciation_focus":
              return (
                <PronunciationFocusBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "listening_discrimination":
              return (
                <ListeningDiscriminationBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "phrase_bank_roleplay":
              return (
                <PhraseBankRoleplayBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "transformation_frame":
              return (
                <TransformationFrameBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "morphology_grid":
              return (
                <MorphologyGridBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "contrast_pair":
              return (
                <ContrastPairBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "timeline_builder":
              return (
                <TimelineBuilderBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "discourse_function":
              return (
                <DiscourseFunctionBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
            case "translation_choice":
              return (
                <TranslationChoiceBlock
                  key={index}
                  block={block}
                  renderState={renderState}
                  blockIndex={index}
                  onEvent={handler}
                />
              );
          }
        })();

        return (
          <div key={key} data-whiteboard-write-line style={whiteboardWriteDelayStyle(writeIndex)}>
            <WhiteboardHeadingContext.Provider
              value={{
                ...headings,
                showSectionLabels:
                  headings.showSectionLabels &&
                  showWhiteboardSectionLabel(
                    block,
                    headings.showTitle ? spec.title : undefined,
                    requiredBlockKeys.length > 1
                  ),
              }}
            >
              {blockElement}
            </WhiteboardHeadingContext.Provider>
          </div>
        );
      })}
    </div>
  );
};
